import React, { ReactNode, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import { useSpring, config, animated, useSpringRef, TransitionFn, useChain } from '@react-spring/web';
import { HandThumbUpIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { Avatar } from 'flowbite-react';
import Image from 'next/image';

import { cn, getAssetSrc, LocaleIcon, useAnalytics, WorkerNavLinkType } from '@restworld/utils-common';

import { Button, LinkBase } from '../../../';
import { Heading } from '@restworld/ui-ds-base';
import { NavLogo } from '..';
import { Container } from '../navigation-bar-styled';
import { NavbarBackgroundType } from '../navigation-bar.types';
import HamburgerComponent from './hamburger-component';

import { AvatarMenuType } from './desktop-overlay-nav';
import NavMenuContent, { NavWorkerInfo } from './nav-menu-content';

interface MobileOverlayNavProps extends AvatarMenuType {
  isAuthenticated?: boolean;
  background?: NavbarBackgroundType;
  navOverlayToggler: React.Dispatch<React.SetStateAction<boolean>>;
  isNavOverlay: boolean;
  onStartLogin: () => void;
  onStartSignup: () => void;
  onLogout?: () => void;
  children?: ReactNode;
  isAmbassador?: boolean;
  primaryItems: WorkerNavLinkType[];
  secondaryItems: WorkerNavLinkType[];
  localeComponent?: ReactNode;
  testId?: string;
  hideNavElement?: boolean;
  workerInfo?: NavWorkerInfo;
  onShowUploadCvModal?: () => void;
}
export type ActiveItemType = {
  subItems: WorkerNavLinkType[];
  label?: string;
  icon?: ReactNode;
  id?: string;
};

export const MobileOverlayNav = memo((props: MobileOverlayNavProps) => {
  const {
    background = 'white',
    isAuthenticated,
    avatarImageURL,
    onAvatarImageError,
    isNavOverlay,
    navOverlayToggler,
    onStartLogin,
    onStartSignup,
    children,
    testId,
    hideNavElement,
    workerInfo,
    onShowUploadCvModal,
  } = props;
  const [activeItem, setActiveItem] = useState<ActiveItemType>();

  const isSubItemAvailable = useMemo(() => Boolean(activeItem?.subItems.length), [activeItem?.subItems.length]);

  const onSelectActiveItem = useCallback((value?: ActiveItemType) => setActiveItem(value), []);

  const onClickMenuToggler = useCallback(() => {
    navOverlayToggler((prev) => !prev);
    onSelectActiveItem();
  }, [navOverlayToggler, onSelectActiveItem]);

  useEffect(() => {
    if (isNavOverlay) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isNavOverlay]);

  const navRef = useSpringRef();
  const liRef = useSpringRef();
  const subRef = useSpringRef();

  const menuAnimation = useSpring({
    ref: navRef,
    config: config.default,
    from: { transform: 'translateY(-100%)' },
    to: {
      transform: isNavOverlay ? 'translateY(0%)' : 'translateY(-100%)',
    },
  });

  // On showMenu, start with nav animation, then nav items
  useChain(isNavOverlay ? [navRef, liRef] : [liRef, navRef], [0, isNavOverlay ? 0.4 : 0.6]);

  // When sub-items are shown, close liRef and open subRef
  useChain(activeItem?.subItems ? [liRef, subRef] : [subRef, liRef], [0, activeItem?.subItems ? 0.4 : 0.6]);

  const isPurpleBackground = useMemo(
    () => background === 'purple-gradient' || background === 'home-mobile-nav',
    [background]
  );
  const showFullHeight = isNavOverlay && !isSubItemAvailable;
  const { t } = useTranslation();

  return (
    <>
      <Container
        justify="between"
        align={showFullHeight ? 'start' : 'center'}
        height={showFullHeight ? 'nav-open' : 'default'}
      >
        <div className="flex flex-col w-full md:gap-4  gap-4 items-start justify-start">
          <StyledNavWrapper $showFullHeight={showFullHeight}>
            <div className="flex flex-col items-start justify-start gap-4 w-fit">
              {isSubItemAvailable ? (
                <div className="flex w-fit md:pl-0 pl-3.5">
                  <Button variant="ghost" onClick={() => onSelectActiveItem()}>
                    <span className="flex items-center gap-1">
                      <ArrowLeftIcon className={cn('h-5 w-5', isPurpleBackground ? 'text-white' : 'text-black')} />
                      <Heading
                        as="span"
                        size={5}
                        weight="semibold"
                        className={isPurpleBackground ? 'text-white' : 'text-black'}
                      >
                        {t('common:back')}
                      </Heading>
                    </span>
                  </Button>
                </div>
              ) : (
                <NavLogo background={background} isSmallLogo={isNavOverlay ? false : Boolean(children)} />
              )}
            </div>
            {!isSubItemAvailable && !isNavOverlay && children}
            <div
              className="flex flex-row items-center justify-start gap-2 w-fit"
              onClick={() => {
                onClickMenuToggler();
              }}
            >
              {isAuthenticated ? (
                <Avatar
                  size="sm"
                  alt="avatar-img"
                  className="md:inline-flex hidden hover:cursor-pointer"
                  rounded
                  color="purple"
                  // Custom img render to ensure non-square source photos get
                  // cropped (object-cover) instead of stretched.
                  img={
                    avatarImageURL
                      ? (imgProps) => (
                          <img
                            {...imgProps}
                            src={avatarImageURL}
                            onError={() => onAvatarImageError?.(avatarImageURL)}
                            className={`${imgProps.className} object-cover`}
                          />
                        )
                      : undefined
                  }
                />
              ) : (
                <div className="lg:flex flex-col items-center  hidden justify-center w-8 h-8 min-w-[32px] bg-primary-100 rounded-full hover:cursor-pointer">
                  <Image
                    src={getAssetSrc('/utils-assets/small-restworld-logo.svg')}
                    alt="restworld"
                    width={24}
                    height={20}
                    className="min-w-[24px]"
                  />
                </div>
              )}
              {hideNavElement ? null : (
                <HamburgerComponent
                  background={background}
                  isOpen={isNavOverlay}
                  data-cy="nav-item-hamburger"
                  className="pr-2"
                />
              )}
            </div>
          </StyledNavWrapper>
        </div>
      </Container>
      <StyledNavOverlay id="mobile-nav-overlay" style={menuAnimation} $showFullHeight={showFullHeight} data-cy={testId}>
        <NavMenuContent
          onCloseMenu={onClickMenuToggler}
          workerInfo={workerInfo}
          onStartSignup={() => {
            onStartSignup();
          }}
          onStartLogin={() => {
            onStartLogin();
          }}
          onShowUploadCvModal={onShowUploadCvModal}
        />
      </StyledNavOverlay>
    </>
  );
});
export default MobileOverlayNav;
export const NavLabelItem = memo(
  ({
    frontNavItems,
    onSelectActiveItem,
    avatarName,
    activeItem,
    isAuthenticated,
    transitions,
    isPrimary,
  }: {
    frontNavItems: WorkerNavLinkType[];
    onSelectActiveItem: (value?: ActiveItemType) => void;
    avatarName?: string;
    activeItem?: ActiveItemType;
    isAuthenticated?: boolean;
    transitions: TransitionFn<WorkerNavLinkType, { opacity: number; transform: string }>;
    isPrimary?: boolean;
  }) => {
    const { show: showIntercom } = useIntercom();

    const handleClick = useCallback(
      ({
        evt,
        subItems,
        label,
        key,
        icon,
      }: {
        evt?: React.SyntheticEvent<HTMLAnchorElement, Event>;
        subItems?: WorkerNavLinkType[];
        label?: string;
        key?: string;
        icon?: ReactNode;
      }) => {
        if (subItems?.length) {
          evt?.preventDefault();
          if (onSelectActiveItem) onSelectActiveItem({ label, subItems, icon, id: key });
        }
        if (key === 'open-intercom') {
          evt?.preventDefault();
          showIntercom();
        }
      },
      [onSelectActiveItem, showIntercom]
    );

    return frontNavItems.length ? (
      <ul className="flex flex-col gap-4">
        {transitions(
          (style, { label, key, subItems, url, icon, isNotVisible, isRecommended, isUnderlined, locale }, _, index) =>
            !isNotVisible ? (
              <animated.li key={key} style={style} className="p-3.5">
                <LinkBase
                  link={!subItems?.length ? url : '#!'}
                  newTab={false}
                  onLinkClick={(evt) => handleClick({ evt, subItems, label, key, icon })}
                  twStyle={[
                    'flex justify-between items-center gap-1',
                    isUnderlined ? 'border-b border-gray-200 pb-8' : '',
                  ]}
                  {...{ locale }}
                >
                  <div className="flex gap-1 items-center">
                    {icon}
                    <Heading
                      as="span"
                      size={5}
                      weight={isRecommended || isPrimary ? 'semibold' : 'normal'}
                      className={isRecommended ? 'text-primary-800' : 'text-black'}
                    >
                      {label}
                    </Heading>
                    {isRecommended ? <RecommendedItem /> : null}
                  </div>
                  {subItems?.length ? <Button icon="ChevronRightIcon" variant="ghost" /> : null}
                </LinkBase>
                {isAuthenticated && activeItem?.label === avatarName && index === 1 ? <hr className="mt-8" /> : null}
              </animated.li>
            ) : null
        )}
      </ul>
    ) : null;
  }
);

export const LocaleItem = ({ localeComponent }: { localeComponent: ReactNode }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-row items-center justify-between w-full px-3.5">
      <div className="flex flex-row items-center gap-1">
        <LocaleIcon />
        <Heading as="span" size={5} weight="semibold">
          {t('common:speech')}
        </Heading>
      </div>
      {localeComponent}
    </div>
  );
};

export const NonLoggedInButton = memo(
  ({
    onStartLogin,
    onStartSignup,
    twStyle,
    testId,
  }: {
    onStartLogin: () => void;
    onStartSignup: () => void;
    twStyle?: string;
    testId?: string;
  }) => {
    const { t } = useTranslation();
    const { trackEvent } = useAnalytics();
    return (
      <NonLoggedInButtonWrapper twStyle={twStyle}>
        <div className="flex flex-col gap-2 w-full md:flex-row md:w-fit">
          <Button
            variant="normal"
            title={t('common:log_in')}
            data-cy={testId}
            onClick={() => {
              onStartLogin();
              trackEvent('initiate_signin', { login_state: false, user_type: null });
            }}
            fullwidth={true}
          />
          <Button
            variant="secondary-outline"
            title={t('common:create_account')}
            onClick={() => {
              onStartSignup();
              // trackEvent('initiate_create_account', {
              //   origin: 'sidebar',
              // });
            }}
            fullwidth
          />
        </div>
      </NonLoggedInButtonWrapper>
    );
  }
);
const NonLoggedInButtonWrapper = ({
  twStyle,
  className,
  ...props
}: { twStyle?: string } & React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn(twStyle, className)} {...props} />
);

const StyledNavWrapper = ({
  $showFullHeight,
  className,
  ...props
}: { $showFullHeight: boolean } & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-row grow-0 gap-4 justify-between items-center w-full',
      $showFullHeight ? 'max-h-[36px]' : '',
      className
    )}
    {...props}
  />
);

export const StyledNavOverlay = ({
  $showFullHeight,
  className,
  children,
  ...props
}: {
  $showFullHeight: boolean;
  className?: string;
  children?: React.ReactNode;
  id?: string;
  style?: React.CSSProperties | Record<string, unknown>;
  'data-cy'?: string;
}) => (
  <animated.div
    className={cn(
      'fixed box-border inset-0 top-0 z-50 flex flex-col justify-between bg-white h-[calc(100svh)] max-w-[100vw]',
      $showFullHeight ? 'top-0 h-[calc(100svh)]' : '',
      className
    )}
    {...props}
  >
    {children}
  </animated.div>
);

const RecommendedItem = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-row gap-0.5 items-center bg-secondary-500 px-2.5 py-0.5 rounded-md">
      <HandThumbUpIcon className="h-3.5 w-3.5 text-primary-800" />
      <Heading as="span" size={6} weight="medium" color="primary" className="text-xs leading-4">
        {t('common:recommended')}
      </Heading>
    </div>
  );
};
